import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch, useStore } from 'react-redux';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  Link as DSLink,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { type FilterState } from '../../../shared/components/SodControls/SodControls';
import SodSearch from '../../../shared/components/SodControls/SodSearch';
import {
  useInvertedLocalize,
  useInViewObserver,
  useLocalize,
  useTypedSelector,
} from '../../../shared/hooks';
import type {
  ActiveAdvancedFilterState,
  AdvancedFilterState,
  RootState,
} from '../../../shared/types';
import { createDevRejectionHandler } from '../../../shared/utils';
import AuditAdvancedFilters from '../AuditAdvancedFilters';
import {
  type AppliedColumnFilters,
  AuditLogColumnFilter,
  type AuditLogColumnFilters,
  getActiveFiltersForColumns,
} from '../AuditLogColumnFilter';
import { useGetAuditColumnsQuery } from '../data-layer/auditLogApi';
import type { AuditLogViewerBodyProps } from '../data-layer/auditLogTypes';
import {
  isFfRoleRequestType,
  resolveAuditLogEndpointDisplayName,
  shouldShowAuditLogRecordingButton,
} from '../data-layer/auditLogUtils';
import {
  auditLogActions,
  selectActiveInsightName,
} from '../data-store/auditLogSlice';
import { useAuditLogData } from '../hooks';
import { useApiCancellation } from '../hooks/useApiCancellation';
import {
  getRecordingPlaybackNotFoundLaunchIds,
  subscribeRecordingPlaybackNotFound,
} from '../SessionRecording';
import { TimeLineBlock } from '../TimeLineBlock';
import type { TimelineData } from '../TimeLineBlock/types';

import './AuditLogViewerBody.css';

// Simple filtering function for timeline data
const applyAdvancedFilters = (
  data: TimelineData[],
  activeFilters: ActiveAdvancedFilterState[]
): TimelineData[] => {
  if (activeFilters.length === 0) {
    return data;
  }

  return data.filter((item: TimelineData) =>
    activeFilters.every((filter) => {
      const filterValues: string[] = filter.options.map((option) =>
        String(option.value)
      );

      switch (filter.value) {
        case 'logType':
          return filterValues.includes(String(item.logType || ''));

        case 'severity':
          return filterValues.includes(String(item.severity || ''));
        case 'entitlementDeviation':
          return filterValues.includes(String(item.entitlementDeviation ?? ''));

        case 'functionNames': {
          const funcs = Array.isArray(item.functionNames)
            ? item.functionNames.map((fn) => String(fn))
            : [];

          return filterValues.some((v) => {
            const searchValue = String(v).toLowerCase();

            return funcs.some((fn) =>
              String(fn).toLowerCase().includes(searchValue)
            );
          });
        }

        case 'businessProcessNames': {
          const bps = Array.isArray(item.businessProcessNames)
            ? item.businessProcessNames.map((bp) => String(bp))
            : [];

          return filterValues.some((v) => {
            const searchValue = String(v).toLowerCase();

            return bps.some((bp) =>
              String(bp).toLowerCase().includes(searchValue)
            );
          });
        }

        case 'reviewActivity':
          return true;
        default:
          return true;
      }
    })
  );
};

/**
 * AuditLogViewerBody Component
 * Body component for the audit log viewer page.
 * Contains the main content area with layout sections.
 */
export interface AuditLogViewerBodyHandle {
  getListSignature: () => string;
  getNextVisibleItemId: (currentId: string | number) => string | number | null;
  scrollItemIntoView: (id: string | number) => void;
  scrollToTop: () => void;
}

const AuditLogViewerBody = forwardRef<
  AuditLogViewerBodyHandle,
  AuditLogViewerBodyProps
>(function AuditLogViewerBody(
  {
    className,
    isInsightsOpen = false,
    onInsightsToggle,
    onTimelineItemClick,
    onTimelineTimestampClick,
    selectedTimelineItemId,
    isDetailPanelOpen = false,
    isRecordingOpen = false,
    onRecordingToggle,
    showInsightsButton = true,
    onColumnFiltersChange,
    advancedFilters,
    activeAdvancedFilters,
    onAdvancedFiltersChange,
    initialActiveFilters,
    onClearCarryOverEntitlement,
    closeDetailPanel,
  }: AuditLogViewerBodyProps,
  ref
): React.ReactElement {
  // Use audit log data hook
  const {
    timelineData: apiTimelineData,
    isLoading: isApiLoading,
    error: apiError,
    refreshAuditLogs,
    searchAuditLogs,
    loadAuditLogsPage,
    totalRecords: apiTotalRecords,
  } = useAuditLogData();

  // Defer refresh to allow batched Redux updates to propagate before
  // `useAuditLogData` builds the next payload from store state.
  const refreshTimerRef = useRef<number | null>(null);
  const refreshAuditLogsDeferred = useCallback(() => {
    if (refreshTimerRef.current != null) {
      window.clearTimeout(refreshTimerRef.current);
    }

    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      refreshAuditLogs();
    }, 0);
  }, [refreshAuditLogs]);

  useEffect(
    () => () => {
      if (refreshTimerRef.current != null) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    },
    []
  );

  // Defer scroll-to-top until after React commit following batched updates.
  const scrollTimerRef = useRef<number | null>(null);
  const scrollListToTopDeferred = useCallback(() => {
    userInitiatedScrollRef.current = false;

    if (scrollTimerRef.current != null) {
      window.clearTimeout(scrollTimerRef.current);
    }

    // setTimeout ensures redux + react updates propagate before rAF scroll.
    scrollTimerRef.current = window.setTimeout(() => {
      scrollTimerRef.current = null;
      requestAnimationFrame(() => {
        listContainerRef.current?.scrollTo({ top: 0 });
      });
    }, 0);
  }, []);

  useEffect(
    () => () => {
      if (scrollTimerRef.current != null) {
        window.clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    },
    []
  );

  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const store = useStore<RootState>();
  const localize = useLocalize();
  const l = useInvertedLocalize();

  // Force remount of search control when filters are cleared
  const [searchMountKey, setSearchMountKey] = useState(0);
  const [isListCleared, setIsListCleared] = useState(false);

  // Extract requestMetadata and API status from Redux
  const requestMetadata = useTypedSelector(
    (s) => s.aag.auditLog.requestMetadata
  );
  const isVideoEnabled = !isFfRoleRequestType(requestMetadata?.requestType);
  const isLoadingRequestReviewDetails = useTypedSelector(
    (s) => s.aag.auditLog.isLoadingRequestReviewDetails
  );
  const requestReviewDetailsError = useTypedSelector(
    (s) => s.aag.auditLog.requestReviewDetailsError
  );

  // Insight filter state
  const insightIds = useTypedSelector((s) => s.aag.auditLog.insightIds);
  const insightName = useTypedSelector(selectActiveInsightName);

  // Get launch detail for recording badge count
  const launchDetail = useTypedSelector((s) => s.aag.auditLog.launchDetail);

  // Sync with playback-not-found set (corrupt/failed recordings) so badge shows only valid count
  const [playbackNotFoundIds, setPlaybackNotFoundIds] = useState<string[]>(() =>
    getRecordingPlaybackNotFoundLaunchIds()
  );

  useEffect(() => {
    if (!isVideoEnabled) {
      setPlaybackNotFoundIds([]);

      return () => {};
    }

    const unsubscribe = subscribeRecordingPlaybackNotFound(() => {
      setPlaybackNotFoundIds(getRecordingPlaybackNotFoundLaunchIds());
    });

    setPlaybackNotFoundIds(getRecordingPlaybackNotFoundLaunchIds());

    return unsubscribe;
  }, [isVideoEnabled]);

  // Count completed launches that are valid (have launchEndDate and are not corrupt/not found)
  const completedLaunchesCount = useMemo(() => {
    if (!isVideoEnabled) {
      return 0;
    }

    if (!Array.isArray(launchDetail) || launchDetail.length === 0) {
      return 0;
    }

    const notFoundSet = new Set(playbackNotFoundIds);

    return launchDetail
      .filter((launch) => Boolean(launch.launchEndDate))
      .filter((launch) => !notFoundSet.has(String(launch.launchId))).length;
  }, [isVideoEnabled, launchDetail, playbackNotFoundIds]);

  const {
    data: apiColumns,
    error: apiFiltersError,
    refetch: refetchColumns,
    isLoading: isColumnsApiLoading,
  } = useGetAuditColumnsQuery(
    {
      view: 'allAuditLogs',
      requestAccessKey: requestMetadata?.requestAccessKey ?? '',
    },
    {
      skip:
        !requestMetadata?.requestAccessKey ||
        requestMetadata.requestAccessKey === '' ||
        isLoadingRequestReviewDetails ||
        !!requestReviewDetailsError,
    }
  );

  const { cancelAllRequests } = useApiCancellation();

  // RTK Query automatically cancels queries when:
  // 1. Component unmounts
  // 2. Query parameters change (requestAccessKey changes)
  // 3. Query is skipped
  // Manual cancellation is only needed for mutations/lazy queries

  // Cancel all mutation requests on unmount
  useEffect(
    () => () => {
      cancelAllRequests();
    },
    [cancelAllRequests]
  );

  // Store columns data and loading state in Redux
  useEffect(() => {
    dispatch(auditLogActions.setColumnsLoading(isColumnsApiLoading));
  }, [dispatch, isColumnsApiLoading]);

  useEffect(() => {
    if (apiColumns) {
      dispatch(auditLogActions.setFetchedColumns(apiColumns));
    }
  }, [dispatch, apiColumns]);

  // Redux as single source of truth for column filters
  const columnFilters = useTypedSelector((s) => s.aag.auditLog.columnFilters);
  const currentAppliedFilters = useTypedSelector(
    (s) => s.aag.auditLog.appliedColumnFilters
  );

  // Initialize advanced filters (empty until API provides)
  const [localAdvancedFilters, setLocalAdvancedFilters] = useState<
    AdvancedFilterState[]
  >(() => advancedFilters ?? []);

  // Initialize active advanced filters state
  const [localActiveAdvancedFilters, setLocalActiveAdvancedFilters] = useState<
    ActiveAdvancedFilterState[]
  >(() => {
    if (Array.isArray(activeAdvancedFilters)) {
      return activeAdvancedFilters;
    }

    if (Array.isArray(initialActiveFilters)) {
      return initialActiveFilters;
    }

    return [] as ActiveAdvancedFilterState[];
  });

  // Replace defaults with API-provided filter pills once available
  useEffect(() => {
    if (apiFiltersError) {
      return;
    }

    const apiObj = (apiColumns ?? {}) as {
      filters?: { id: string; localizationKey: string; isDefault: boolean }[];
    };
    const apiFilters = Array.isArray(apiObj.filters) ? apiObj.filters : [];

    if (apiFilters.length === 0) {
      return;
    }

    const mapped: AdvancedFilterState[] = apiFilters.map((f) => ({
      value: f.id,
      label: localize(f.localizationKey) || f.id,
      method: 'SEARCH',
      isChecked: Boolean(f.isDefault),
      options: [],
    }));

    setLocalAdvancedFilters(mapped);
  }, [apiColumns, apiFiltersError, localize, dispatch]);

  // Bootstrap column filters from Columns API once (Redux as source of truth)
  useEffect(() => {
    if (apiFiltersError) {
      return;
    }

    // Already initialized with real options
    if (
      columnFilters.some(
        (f) => Array.isArray(f.options) && f.options.length > 0
      )
    ) {
      return;
    }

    interface AuditColumnItem {
      id: string;
      localizationKey: string;
      isDefault: boolean;
    }
    const {
      securityAuditLog = [],
      changeAuditLog = [],
      tcodeAuditLog = [],
      systemAuditLog = [],
    } = (apiColumns ?? {}) as Partial<Record<string, AuditColumnItem[]>>;

    const hasData =
      (Array.isArray(securityAuditLog) && securityAuditLog.length > 0) ||
      (Array.isArray(changeAuditLog) && changeAuditLog.length > 0) ||
      (Array.isArray(tcodeAuditLog) && tcodeAuditLog.length > 0) ||
      (Array.isArray(systemAuditLog) && systemAuditLog.length > 0);

    if (!hasData) {
      return;
    }

    const apiToGroupMap = {
      securityAuditLog: { label: 'Security Audit Log', value: 'SEC_AUDIT_LOG' },
      changeAuditLog: { label: 'Change Log', value: 'CHANGE_LOG' },
      tcodeAuditLog: { label: 'T-Code Log', value: 'TCODE_LOG' },
      systemAuditLog: { label: 'System Log', value: 'SYSTEM_LOG' },
    } as const;

    type ApiKey = keyof typeof apiToGroupMap;

    const groupsMap: Record<ApiKey, AuditColumnItem[]> = {
      securityAuditLog,
      changeAuditLog,
      tcodeAuditLog,
      systemAuditLog,
    };

    const normalizedFilters: AuditLogColumnFilters = (
      Object.keys(apiToGroupMap) as ApiKey[]
    )
      .filter(
        (apiKey) =>
          Array.isArray(groupsMap[apiKey]) && groupsMap[apiKey].length > 0
      )
      .map((apiKey) => {
        const group = apiToGroupMap[apiKey];
        const source = groupsMap[apiKey];

        const options = source
          .map((c) => ({
            label: localize(c.localizationKey),
            value: `${group.value}-${c.id}`,
            isChecked: c.isDefault,
          }))
          .sort((a, b) => Number(b.isChecked) - Number(a.isChecked));

        return {
          label: l(group.label),
          value: group.value,
          isDisabled: false,
          options,
        };
      });

    if (normalizedFilters.length === 0) {
      return;
    }

    dispatch(auditLogActions.setColumnFilters(normalizedFilters));

    const newApplied = getActiveFiltersForColumns(normalizedFilters);

    dispatch(auditLogActions.setAppliedColumnFilters(newApplied));

    if (onColumnFiltersChange) {
      onColumnFiltersChange(newApplied);
    }
  }, [
    apiColumns,
    apiFiltersError,
    columnFilters,
    localize,
    l,
    dispatch,
    onColumnFiltersChange,
  ]);

  // Track when we're setting filters from initialActiveFilters to prevent sync loop
  const isSettingFromInitialFiltersRef = useRef(false);
  const prevInitialActiveFiltersRef = useRef<
    ActiveAdvancedFilterState[] | undefined
  >(undefined);
  // Track when we're applying carryOverAuditStatus to prevent sync loop during initial application
  const isApplyingCarryOverRef = useRef(false);

  // Apply incoming initialActiveFilters when provided (e.g., from Transaction Overview carry over)
  // Immediately sync to Redux to ensure payload includes the filter before any search is triggered
  // Also handle clearing when initialActiveFilters becomes undefined/empty
  useEffect(() => {
    const prev = prevInitialActiveFiltersRef.current;
    const current = initialActiveFilters;

    // Detect when initialActiveFilters changes from array to undefined (Clear button clicked)
    const wasArray = Array.isArray(prev) && prev.length > 0;
    const isNowUndefined =
      current === undefined || (Array.isArray(current) && current.length === 0);

    if (wasArray && isNowUndefined) {
      // Clear filters when initialActiveFilters is cleared
      isSettingFromInitialFiltersRef.current = true;
      setLocalActiveAdvancedFilters([]);
      batch(() => {
        dispatch(auditLogActions.setActiveFilters([]));
      });
      setTimeout(() => {
        isSettingFromInitialFiltersRef.current = false;
      }, 0);
    } else if (current && Array.isArray(current) && current.length > 0) {
      // Set filters when initialActiveFilters is provided
      isSettingFromInitialFiltersRef.current = true;
      setLocalActiveAdvancedFilters(current);
      // Immediately dispatch to Redux to ensure buildSearchPayload reads the correct filters
      batch(() => {
        dispatch(auditLogActions.setActiveFilters(current));
      });
      // Reset flag after Redux state has propagated and refs are updated
      setTimeout(() => {
        isSettingFromInitialFiltersRef.current = false;
      }, 0);
    }

    // Update ref for next comparison
    prevInitialActiveFiltersRef.current = current;
  }, [initialActiveFilters, dispatch]);

  // Advanced filters UI state - open by default on initial load
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(true);

  // Track initial mount to prevent empty state flash
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Choose the correct filter set based on active tab context
  const activeTab = 'all-logs' as const;
  const carryOverAuditStatus = useTypedSelector(
    (s) => s.aag.auditLog.carryOverAuditStatus
  );

  const currentActiveAdvancedFilters = useMemo(() => {
    if (activeAdvancedFilters) {
      return activeAdvancedFilters;
    }

    // In All Logs, always use localActiveAdvancedFilters (never entitlement filters)
    return localActiveAdvancedFilters;
  }, [activeAdvancedFilters, localActiveAdvancedFilters]);

  // Apply carry-over audit status into localActiveAdvancedFilters (All Logs only)
  useEffect(() => {
    if (!carryOverAuditStatus) {
      isApplyingCarryOverRef.current = false;

      return;
    }

    // Mark that we're applying carry-over to prevent sync loop
    isApplyingCarryOverRef.current = true;

    // Read latest Redux activeFilters directly from store to ensure we sync with what Smart Filters just set
    const currentState = store.getState();
    const currentReduxFilters = currentState.aag.auditLog.activeFilters;

    // Use Redux filters as base (they already have the correct status from Smart Filters)
    // Filter out any existing auditLogStatuses and add the carried one
    const baseFilters = currentReduxFilters.filter(
      (f) => f.value !== 'auditLogStatuses'
    );

    setLocalActiveAdvancedFilters([
      ...baseFilters,
      {
        value: 'auditLogStatuses',
        method: 'SEARCH',
        options: [
          {
            label: carryOverAuditStatus,
            value: carryOverAuditStatus,
          },
        ],
      },
    ]);

    // Force-open filters panel
    setIsAdvancedFiltersOpen(true);

    // Reset flag after a tick to allow normal sync to resume
    setTimeout(() => {
      isApplyingCarryOverRef.current = false;
    }, 0);
  }, [activeTab, carryOverAuditStatus, store]);

  // Compute the effective active filters from both checked pills and selected options
  const computedActiveAdvancedFilters = useMemo(() => {
    const byKey: Partial<Record<string, ActiveAdvancedFilterState>> = {};

    // Seed from option selections
    localActiveAdvancedFilters.forEach((f) => {
      byKey[f.value] = {
        value: f.value,
        method: f.method,
        options: Array.isArray(f.options) ? f.options : [],
      };
    });

    // Ensure checked pills appear in active, even if no options selected
    localAdvancedFilters.forEach((f) => {
      if (f.isChecked) {
        if (!byKey[f.value]) {
          byKey[f.value] = { value: f.value, method: 'SEARCH', options: [] };
        }
      }
    });

    return Object.values(byKey).filter(
      (item): item is ActiveAdvancedFilterState => Boolean(item)
    );
  }, [localAdvancedFilters, localActiveAdvancedFilters]);

  // Redux dispatch helpers for column filter updates
  const setColumnFiltersRedux = useCallback(
    (filters: AuditLogColumnFilters) => {
      dispatch(auditLogActions.setColumnFilters(filters));
    },
    [dispatch]
  );

  const setAppliedColumnFiltersRedux = useCallback(
    (filters: AppliedColumnFilters) => {
      dispatch(auditLogActions.setAppliedColumnFilters(filters));
    },
    [dispatch]
  );

  // Handle column filter changes
  const handleColumnFiltersChange = (filters: AppliedColumnFilters) => {
    dispatch(auditLogActions.setAppliedColumnFilters(filters));

    if (onColumnFiltersChange) {
      onColumnFiltersChange(filters);
    }
  };

  // Handle filter enable/disable changes
  const handleFilterChange = (filter: FilterState) => {
    setLocalAdvancedFilters((prev) =>
      prev.map((f) =>
        f.value === filter.value ? { ...f, isChecked: !f.isChecked } : f
      )
    );

    if (!filter.isChecked) {
      // Remove from active filters when unchecked
      setLocalActiveAdvancedFilters((prev) => {
        const next = prev.filter((af) => af.value !== filter.value);

        return next;
      });
    }
  };

  // Handle filter option selections
  const handleOptionsClick = (
    key: string,
    method: string,
    selectedOptions: MenuOption[]
  ) => {
    setLocalActiveAdvancedFilters((prev) => {
      const existingIndex = prev.findIndex((f) => f.value === key);

      if (selectedOptions.length === 0) {
        // Remove filter if no options selected
        const next = prev.filter((f) => f.value !== key);

        return next;
      }

      const newActiveFilter: ActiveAdvancedFilterState = {
        value: key,
        method,
        options: selectedOptions,
      };

      if (existingIndex >= 0) {
        const updated = [...prev];

        updated[existingIndex] = newActiveFilter;

        return updated;
      }

      const next = [...prev, newActiveFilter];

      return next;
    });
  };

  // Handle filter search for searchable filters
  const handleFilterSearch = useCallback(
    (): Promise<MenuOption[]> =>
      // Implement remote search here when available
      Promise.resolve([]),
    []
  );

  // Handle clearing all advanced filters
  const handleClearAllAdvancedFilters = () => {
    // Clear only applied selections; keep filters visible and panel height preserved
    setLocalActiveAdvancedFilters([]);

    if (onAdvancedFiltersChange) {
      onAdvancedFiltersChange([]);
    }

    // Also clear carry-over so it does not re-apply on rerender
    if (onClearCarryOverEntitlement) {
      onClearCarryOverEntitlement();
    }

    // Clear filters, carried audit status and reset cursor in Redux
    batch(() => {
      dispatch(auditLogActions.setActiveFilters([]));
      dispatch(auditLogActions.setCarryOverAuditStatus(null));
      dispatch(auditLogActions.setSearchQuery(''));
      dispatch(auditLogActions.resetSearchAfter());
    });

    // Also remount the search input so its internal state resets immediately
    setSearchMountKey((k) => k + 1);
  };

  // Flag to skip the next reduxActiveFilters-driven refresh when search triggers a manual fetch
  const skipNextRefreshRef = useRef(false);
  // Guard pagination right after criteria changes (filters/search)
  const criteriaChangedRef = useRef(false);

  // Handle search
  const handleSearch = useCallback(
    (searchText: string): void => {
      // Reset cursor and clear data before searching
      batch(() => {
        setIsListCleared(true);
        dispatch(auditLogActions.resetSearchAfter());
        dispatch(auditLogActions.clearData());
        dispatch(auditLogActions.setLoading(true));
      });
      // Reset scroll position before refetch
      scrollListToTopDeferred();
      criteriaChangedRef.current = true;
      // Call the search API with the search text
      skipNextRefreshRef.current = true;
      searchAuditLogs(searchText).catch(() => {
        // Error handling is already done in the hook
      });
    },
    [searchAuditLogs, dispatch, scrollListToTopDeferred]
  );

  // Notify parent of advanced filter changes and persist in Redux
  // Track bootstrap completion for gating search refresh
  const isBootstrappedRef = useRef(false);
  const reduxFilters = useTypedSelector((s) => s.aag.auditLog.filters);
  const reduxActiveFiltersForCompare = useTypedSelector(
    (s) => s.aag.auditLog.activeFilters
  );

  useEffect(() => {
    if (onAdvancedFiltersChange) {
      onAdvancedFiltersChange(computedActiveAdvancedFilters);
    }

    // Skip sync if we're currently setting from initialActiveFilters to prevent loop
    // But only skip if initialActiveFilters is actually set (not undefined)
    // This allows normal user filter changes to sync even if flag was recently set
    if (
      isSettingFromInitialFiltersRef.current &&
      initialActiveFilters &&
      Array.isArray(initialActiveFilters) &&
      initialActiveFilters.length > 0
    ) {
      return;
    }

    // Skip sync if we're currently applying carryOverAuditStatus to prevent loop
    // But allow sync once carryOver is applied, so users can add additional filters
    if (isApplyingCarryOverRef.current) {
      return;
    }

    // Deep-compare against current redux to avoid no-op dispatches

    interface SigOption {
      value?: unknown;
    }
    interface SigItem {
      value: string;
      method?: string;
      options?: readonly SigOption[];
    }

    const toSignature = (arr: readonly SigItem[] = []): string => {
      const mapped = arr.map((f) => ({
        value: f.value,
        method: f.method,
        options: (Array.isArray(f.options) ? f.options : [])
          .map((o: SigOption) => String(o.value ?? ''))
          .sort(),
      }));

      return JSON.stringify(
        mapped.sort((a, b) => a.value.localeCompare(b.value))
      );
    };

    const nextFiltersSig = toSignature(localAdvancedFilters);
    const currentFiltersSig = toSignature(reduxFilters);
    const nextActiveSig = toSignature(computedActiveAdvancedFilters);
    const currentActiveSig = toSignature(reduxActiveFiltersForCompare);

    if (
      nextFiltersSig === currentFiltersSig &&
      nextActiveSig === currentActiveSig
    ) {
      // Already in store; mark bootstrapped and skip
      if (!isBootstrappedRef.current) {
        isBootstrappedRef.current = true;
      }

      return;
    }

    batch(() => {
      dispatch(auditLogActions.setFilters(localAdvancedFilters));
      dispatch(auditLogActions.setActiveFilters(computedActiveAdvancedFilters));
    });

    if (!isBootstrappedRef.current) {
      isBootstrappedRef.current = true;
    }
  }, [
    computedActiveAdvancedFilters,
    localAdvancedFilters,
    onAdvancedFiltersChange,
    dispatch,
    reduxFilters,
    reduxActiveFiltersForCompare,
    carryOverAuditStatus,
    store,
    initialActiveFilters,
  ]);

  // Trigger search after Redux activeFilters updates (ensures payload reflects latest)
  const reduxActiveFilters = useTypedSelector(
    (s) => s.aag.auditLog.activeFilters
  );
  const reduxSearchQuery = useTypedSelector((s) => s.aag.auditLog.searchQuery);

  // Get searchAfter cursor from Redux for pagination
  const searchAfter = useTypedSelector((s) => s.aag.auditLog.searchAfter);

  const prevActiveFiltersSigRef = useRef<string>('');
  const prevSearchQueryRef = useRef<string>('');
  const hasInitializedCriteriaSyncRef = useRef(false);

  useEffect(() => {
    const nextActiveSig = JSON.stringify(reduxActiveFilters);
    const nextSearch = String(reduxSearchQuery);

    if (!hasInitializedCriteriaSyncRef.current) {
      hasInitializedCriteriaSyncRef.current = true;
      prevActiveFiltersSigRef.current = nextActiveSig;
      prevSearchQueryRef.current = nextSearch;

      return;
    }

    const filtersChanged = nextActiveSig !== prevActiveFiltersSigRef.current;
    const searchChanged = nextSearch !== prevSearchQueryRef.current;

    if (filtersChanged) {
      // Clear list and fetch fresh data for any filter change
      batch(() => {
        setIsListCleared(true);
        dispatch(auditLogActions.resetSearchAfter());
        dispatch(auditLogActions.clearData());
        dispatch(auditLogActions.setLoading(true));
      });
      // Reset scroll position before refetch
      scrollListToTopDeferred();
      criteriaChangedRef.current = true;
      refreshAuditLogsDeferred();
    } else if (searchChanged) {
      // Always reset cursor on search text change
      batch(() => {
        dispatch(auditLogActions.resetSearchAfter());
      });
      // Reset scroll position
      scrollListToTopDeferred();

      // Do not trigger auto refresh on search text change here.
      // The explicit handleSearch path handles calling the API.
      if (skipNextRefreshRef.current) {
        skipNextRefreshRef.current = false;
      }
    }

    prevActiveFiltersSigRef.current = nextActiveSig;
    prevSearchQueryRef.current = nextSearch;
  }, [
    reduxActiveFilters,
    reduxSearchQuery,
    refreshAuditLogs,
    dispatch,
    scrollListToTopDeferred,
    refreshAuditLogsDeferred,
  ]);

  // Use API data only
  const allTimelineData = useMemo((): TimelineData[] => {
    if (Array.isArray(apiTimelineData)) {
      return apiTimelineData;
    }

    return [];
  }, [apiTimelineData]);

  // Mark as initially loaded when we have data or finished loading
  useEffect(() => {
    if ((allTimelineData.length > 0 || !isApiLoading) && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true);
    }

    if (!isApiLoading) {
      setIsListCleared(false);
    }
  }, [allTimelineData.length, isApiLoading, hasInitiallyLoaded]);

  // Reset criteria-changed guard after first load completes
  const prevApiLoadingRef = useRef(false);

  useEffect(() => {
    const wasLoading = prevApiLoadingRef.current;

    if (wasLoading && !isApiLoading && criteriaChangedRef.current) {
      criteriaChangedRef.current = false;
    }

    prevApiLoadingRef.current = isApiLoading;
  }, [isApiLoading]);

  // Column filters only affect column visibility within items, not item visibility
  const columnFilteredData = useMemo(
    (): TimelineData[] => allTimelineData,
    [allTimelineData]
  );

  // Apply advanced filters to column-filtered data
  const filteredTimelineData = useMemo((): TimelineData[] => {
    if (currentActiveAdvancedFilters.length === 0) {
      return columnFilteredData;
    }

    return applyAdvancedFilters(
      columnFilteredData,
      currentActiveAdvancedFilters
    );
  }, [columnFilteredData, currentActiveAdvancedFilters]);

  const handleInsightsToggle = () => {
    // Close recording panel if insights is being opened - delegate to parent
    if (isRecordingOpen && onRecordingToggle) {
      onRecordingToggle();
    }

    // Call the original insights toggle
    if (onInsightsToggle) {
      onInsightsToggle();
    }
  };

  // Clear insight filter and refetch
  const handleClearInsightFilter = () => {
    batch(() => {
      dispatch(auditLogActions.setInsightIds([]));
      dispatch(auditLogActions.setSuppressInitialFetch(true));
      dispatch(auditLogActions.setLoading(true));
      dispatch(auditLogActions.resetSearchAfter());
      dispatch(auditLogActions.clearData());
    });
    // Scroll to top and refetch
    scrollListToTopDeferred();
    refreshAuditLogsDeferred();

    // Close detail panel via lifted callback and insights panel per requirement
    if (typeof closeDetailPanel === 'function') {
      closeDetailPanel();
    }

    if (isInsightsOpen && onInsightsToggle) {
      onInsightsToggle();
    }
  };

  // =====================
  // Infinite Scroll (API-driven offset-based)
  // =====================
  const ALL_LOGS_PAGE_SIZE = 50;
  const [isAllLogsObserverInView, setIsAllLogsObserverInView] = useState(false);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  // Only paginate after explicit user scroll following criteria changes
  const userInitiatedScrollRef = useRef(false);
  const lastSignatureRef = useRef<string>('');

  useEffect(() => {
    lastSignatureRef.current = JSON.stringify(
      filteredTimelineData.map((i) => String(i.uniqueLogId))
    );
  }, [filteredTimelineData]);

  useImperativeHandle(
    ref,
    () => ({
      getListSignature: () => lastSignatureRef.current,
      getNextVisibleItemId: (currentId: string | number) => {
        const ids = filteredTimelineData.map((i) => String(i.uniqueLogId));
        const idx = ids.indexOf(String(currentId));

        if (idx === -1) {
          return null;
        }

        if (idx + 1 >= ids.length) {
          return null;
        }

        return ids[idx + 1];
      },
      scrollItemIntoView: (id: string | number) => {
        const container = listContainerRef.current;

        if (!container) {
          return;
        }

        const el = container.querySelector(`[data-unique-id="${String(id)}"]`);

        if (
          el instanceof HTMLElement &&
          typeof el.scrollIntoView === 'function'
        ) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      },
      scrollToTop: () => {
        scrollListToTopDeferred();
      },
    }),
    [filteredTimelineData, scrollListToTopDeferred]
  );

  const listObserverElement = useInViewObserver(
    ({ isInView }) => {
      setIsAllLogsObserverInView(isInView);
    },
    { root: listContainerRef.current }
  );

  // Trigger next page when sentinel becomes visible inside the scroll container
  useEffect(() => {
    const hasMorePages = allTimelineData.length < apiTotalRecords;
    const hasValidCursor =
      searchAfter !== null &&
      Array.isArray(searchAfter) &&
      searchAfter.length > 0;
    const canLoadMore = hasMorePages && hasValidCursor;

    if (
      isAllLogsObserverInView &&
      !isApiLoading &&
      canLoadMore &&
      !criteriaChangedRef.current &&
      userInitiatedScrollRef.current
    ) {
      loadAuditLogsPage(searchAfter, ALL_LOGS_PAGE_SIZE).catch(
        createDevRejectionHandler('AuditLogViewerBody: loadAuditLogsPage')
      );
    }
  }, [
    isAllLogsObserverInView,
    isApiLoading,
    allTimelineData.length,
    apiTotalRecords,
    searchAfter,
    loadAuditLogsPage,
  ]);

  // Auto-fetch while container is not scrollable (content doesn't fill viewport)
  useEffect(() => {
    const container = listContainerRef.current;

    if (!container) {
      return;
    }

    const hasMorePages = allTimelineData.length < apiTotalRecords;
    const hasValidCursor =
      searchAfter !== null &&
      Array.isArray(searchAfter) &&
      searchAfter.length > 0;
    const canLoadMore = hasMorePages && hasValidCursor;
    const needsMore = container.scrollHeight <= container.clientHeight;

    if (
      needsMore &&
      canLoadMore &&
      !isApiLoading &&
      !criteriaChangedRef.current &&
      userInitiatedScrollRef.current
    ) {
      loadAuditLogsPage(searchAfter, ALL_LOGS_PAGE_SIZE).catch(
        createDevRejectionHandler('AuditLogViewerBody: loadAuditLogsPage')
      );
    }
  }, [
    allTimelineData.length,
    apiTotalRecords,
    searchAfter,
    isApiLoading,
    loadAuditLogsPage,
  ]);

  // Extract endpoint from Redux requestMetadata
  const reqMeta = useTypedSelector((s) => s.aag.auditLog.requestMetadata);
  const auditEndpoint = resolveAuditLogEndpointDisplayName(reqMeta);

  return (
    <Box
      className={cx('Sod-AuditLogViewerBody', className, {
        'Sod-AuditLogViewerBody--withRecording': isRecordingOpen,
        'Sod-AuditLogViewerBody--withDetailPanel': isDetailPanelOpen,
      })}>
      {/* Main Content Area */}
      <Box
        className={cx('Sod-AuditLogViewerBody-mainContent', {
          'Sod-AuditLogViewerBody-mainContent--withInsights': isInsightsOpen,
          'Sod-AuditLogViewerBody-mainContent--withRecording': isRecordingOpen,
          'Sod-AuditLogViewerBody-mainContent--withDetailPanel':
            isDetailPanelOpen,
        })}>
        <Box className='Sod-AuditLogViewerBodylistControls'>
          <Box className='Sod-AuditLogViewerBody-listControls-topRow'>
            <Box className='Sod-AuditLogViewerBody-listControls-topRow-list-controls'>
              {showInsightsButton && (
                <Box className='Sod-AuditLogViewerBody-listControls-topRow-listControls-Savi'>
                  <ButtonSelect
                    color='savi'
                    kind='icon'
                    ariaLabel={l('Findings')}
                    onClick={handleInsightsToggle}
                    size='medium'
                    prefixIcon={<Icon kind='SaviCompanion' />}
                  />
                </Box>
              )}
              <ButtonSelect
                kind='rectangle'
                label={l('Filter')}
                isOpen={isAdvancedFiltersOpen}
                size='medium'
                prefixIcon={<Icon kind='Filter' />}
                BadgeComp={
                  currentActiveAdvancedFilters.length > 0 ? (
                    <Badge
                      kind='number'
                      color='primary'
                      size='small'
                      number={currentActiveAdvancedFilters.length}
                    />
                  ) : null
                }
                onClick={() => setIsAdvancedFiltersOpen((prev) => !prev)}
                isVisualCueEnabled={false}
              />
              <AuditLogColumnFilter
                columnFilters={columnFilters}
                setColumnFilters={setColumnFiltersRedux}
                appliedColumnFilters={currentAppliedFilters}
                setAppliedColumnFilters={setAppliedColumnFiltersRedux}
                isButtonKindIcon={false}
                onColumnChange={handleColumnFiltersChange}
              />
              <SodSearch
                key={searchMountKey}
                searchPlaceholder={l('Search')}
                handleSearch={handleSearch}
                inlineMesssage='Type more than 2 characters to see results.'
                searchDebounce={300}
                isDisabled={false}
                inputFieldClassName='AuditLogViewerBody-searchInput'
              />
            </Box>
            {!isRecordingOpen &&
              isVideoEnabled &&
              shouldShowAuditLogRecordingButton(
                requestMetadata?.requestType
              ) && (
                <Box className='Sod-AuditLogViewerBody-listControls-topRow-recording-button'>
                  <ButtonSelect
                    kind='rectangle'
                    label={l('Recording')}
                    isOpen={isRecordingOpen}
                    size='medium'
                    prefixIcon={<Icon kind='Video' />}
                    isVisualCueEnabled={false}
                    onClick={onRecordingToggle}
                    BadgeComp={
                      completedLaunchesCount > 0 ? (
                        <Badge
                          kind='number'
                          color='primary'
                          size='small'
                          number={completedLaunchesCount}
                        />
                      ) : null
                    }
                  />
                </Box>
              )}
          </Box>

          {/* Advanced Filters */}
          <AuditAdvancedFilters
            activeFilters={currentActiveAdvancedFilters}
            handleFilterChange={handleFilterChange}
            handleOptionsClick={handleOptionsClick}
            handleFilterSearch={handleFilterSearch}
            handleClearBtn={handleClearAllAdvancedFilters}
            isClearBtn={currentActiveAdvancedFilters.length > 0}
            searchDebounce={300}
            className={
              isAdvancedFiltersOpen
                ? 'SodAdvancedFilters--isOpen'
                : 'SodAdvancedFilters--isClose'
            }
            endpoint={auditEndpoint}
          />
        </Box>
        <Box className='Sod-AuditLogViewerBody-number-count'>
          <Typography lineLimit={3} kind='body2'>
            <span className=''>
              {currentActiveAdvancedFilters.length > 0
                ? `${filteredTimelineData.length} of ${apiTotalRecords} items`
                : `${apiTotalRecords} items`}
            </span>
          </Typography>
          {Array.isArray(insightIds) &&
            insightIds.length > 0 &&
            insightName && (
              <Box className='Sod-AuditLogViewerBody-number-count-insight'>
                <Typography lineLimit={3} kind='body2'>
                  {l('Displaying items related to the insight: ')}{' '}
                  <span className='Sod-AuditLogViewerBody-number-count-insight-text'>
                    {insightName}
                  </span>
                </Typography>
                <DSLink
                  text={l('Clear')}
                  size='small'
                  kind='button'
                  onClick={handleClearInsightFilter}
                />
              </Box>
            )}
        </Box>
        <div
          className='Sod-AuditLogViewerBody-list'
          ref={listContainerRef}
          onScroll={() => {
            // Close any open popovers by dispatching a custom event
            window.dispatchEvent(new CustomEvent('closePopovers'));
            userInitiatedScrollRef.current = true;
          }}>
          <TimeLineBlock
            timelineData={isListCleared ? [] : filteredTimelineData}
            onItemClick={(item, tab) => onTimelineItemClick?.(item, tab)}
            onTimestampClick={
              isVideoEnabled
                ? (item) => onTimelineTimestampClick?.(item)
                : undefined
            }
            selectedItemId={selectedTimelineItemId}
            appliedColumnFilters={currentAppliedFilters}
            isLoading={
              isListCleared ||
              !hasInitiallyLoaded ||
              (isApiLoading && allTimelineData.length === 0)
            }
            error={apiError ?? (apiFiltersError ? 'filters_error' : null)}
            onRetry={() => {
              refreshAuditLogs();
              refetchColumns().catch(
                createDevRejectionHandler('AuditLogViewerBody: refetchColumns')
              );
            }}
            isDetailPanelOpen={isDetailPanelOpen}
            isRecordingOpen={isRecordingOpen}
            onClearAllFilters={handleClearAllAdvancedFilters}
          />
          {/* Sentinel for infinite scroll pagination */}
          {allTimelineData.length < apiTotalRecords &&
          searchAfter !== null &&
          Array.isArray(searchAfter) &&
          searchAfter.length > 0
            ? listObserverElement
            : null}
        </div>
      </Box>
    </Box>
  );
});

AuditLogViewerBody.defaultProps = {
  className: undefined,
  isInsightsOpen: false,
  onInsightsToggle: undefined,
  onTimelineItemClick: undefined,
  onTimelineTimestampClick: undefined,
  selectedTimelineItemId: undefined,
  isDetailPanelOpen: false,
  isRecordingOpen: false,
  onRecordingToggle: undefined,
  showInsightsButton: true,
  appliedColumnFilters: undefined,
  onColumnFiltersChange: undefined,
  timelineData: [],
  advancedFilters: undefined,
  activeAdvancedFilters: undefined,
  onAdvancedFiltersChange: undefined,
};

export default AuditLogViewerBody;
